/**
 * CreatingPlan - Interim page shown immediately after clicking Create
 *
 * Shows an engaging loading state while the task is being created.
 * Once the task ID is available, redirects to the actual PlanView.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  stem:       '#E8C7A3',
  roof:       '#C76A3B',
  r2: '6px', r3: '10px', r4: '14px', r5: '20px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sh3: '0 3px 0 rgba(46,33,9,0.05), 0 20px 36px -12px rgba(46,33,9,0.18)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('cc-plan-keyframes')) {
  const style = document.createElement('style');
  style.id = 'cc-plan-keyframes';
  style.textContent = `
    @keyframes cc-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes cc-pulse {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 1; }
    }
    @keyframes cc-progress {
      0%   { width: 4%; }
      20%  { width: 28%; }
      45%  { width: 52%; }
      70%  { width: 74%; }
      90%  { width: 88%; }
      100% { width: 92%; }
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Cottage SVG illustration
// ---------------------------------------------------------------------------

const CottageIllustration = () => (
  <svg
    width="72"
    height="64"
    viewBox="0 0 72 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginBottom: '20px', display: 'block', margin: '0 auto 20px' }}
  >
    {/* Chimney */}
    <rect x="48" y="10" width="8" height="14" rx="2" fill="#C76A3B" />
    <rect x="46" y="8" width="12" height="4" rx="1" fill="#A35429" />
    {/* Smoke puffs */}
    <circle cx="52" cy="5" r="3" fill={T.rule} opacity="0.6" />
    <circle cx="54" cy="2" r="2" fill={T.rule} opacity="0.35" />
    {/* Roof */}
    <polygon points="4,32 36,6 68,32" fill="#C76A3B" />
    <polygon points="4,32 36,10 68,32" fill="#A35429" opacity="0.25" />
    {/* Walls */}
    <rect x="8" y="30" width="56" height="34" rx="2" fill="#FFFBEC" />
    <rect x="8" y="30" width="56" height="34" rx="2" stroke={T.ruleStrong} strokeWidth="1.5" />
    {/* Door */}
    <rect x="28" y="44" width="16" height="20" rx="3" fill={T.paperDeep} stroke={T.ruleStrong} strokeWidth="1.5" />
    <circle cx="41" cy="54" r="1.5" fill={T.orangeDark} />
    {/* Left window */}
    <rect x="12" y="36" width="12" height="10" rx="2" fill={T.paperSoft} stroke={T.ruleStrong} strokeWidth="1.5" />
    <line x1="18" y1="36" x2="18" y2="46" stroke={T.ruleStrong} strokeWidth="1" />
    <line x1="12" y1="41" x2="24" y2="41" stroke={T.ruleStrong} strokeWidth="1" />
    {/* Right window */}
    <rect x="48" y="36" width="12" height="10" rx="2" fill={T.paperSoft} stroke={T.ruleStrong} strokeWidth="1.5" />
    <line x1="54" y1="36" x2="54" y2="46" stroke={T.ruleStrong} strokeWidth="1" />
    <line x1="48" y1="41" x2="60" y2="41" stroke={T.ruleStrong} strokeWidth="1" />
    {/* Ground */}
    <path d="M2 64 Q36 58 70 64" stroke={T.mossSoft} strokeWidth="2" fill="none" />
    {/* Little bush */}
    <circle cx="6" cy="61" r="4" fill={T.moss} opacity="0.5" />
    <circle cx="66" cy="61" r="4" fill={T.moss} opacity="0.5" />
  </svg>
);

// ---------------------------------------------------------------------------
// Step status indicators
// ---------------------------------------------------------------------------

const StepIcon = ({ status }) => {
  if (status === 'done') {
    return (
      <div style={{
        width: '22px',
        height: '22px',
        borderRadius: T.full,
        background: T.moss,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
          <path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  }
  if (status === 'active') {
    return (
      <div style={{
        width: '22px',
        height: '22px',
        borderRadius: T.full,
        border: `2.5px solid ${T.orange}`,
        borderTopColor: 'transparent',
        flexShrink: 0,
        animation: 'cc-spin 0.8s linear infinite',
      }} />
    );
  }
  // pending
  return (
    <div style={{
      width: '22px',
      height: '22px',
      borderRadius: T.full,
      border: `2px dashed ${T.ruleStrong}`,
      flexShrink: 0,
    }} />
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const STEPS = [
  'Breaking down what you described',
  'Identifying core features and gaps',
  'Drafting stories and acceptance criteria',
  'Flagging decisions and open questions',
  'Assembling your plan',
];

// Approximate cumulative progress for each active step (0–1)
const STEP_PROGRESS = [0.12, 0.34, 0.56, 0.76, 0.90];

const CreatingPlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { description, isCreating } = location.state || {};

  const [activeStep, setActiveStep] = useState(0);

  // Advance through steps on a timer to give feedback even without real events
  useEffect(() => {
    if (activeStep >= STEPS.length - 1) return;
    const delay = activeStep === 0 ? 3200 : 4800;
    const t = setTimeout(() => setActiveStep(s => s + 1), delay);
    return () => clearTimeout(t);
  }, [activeStep]);

  // Redirect if somehow arrived without state
  useEffect(() => {
    if (!isCreating && !description) {
      navigate('/plans/create');
    }
  }, [isCreating, description, navigate]);

  const progress = STEP_PROGRESS[Math.min(activeStep, STEP_PROGRESS.length - 1)];
  const truncatedDesc = description
    ? description.substring(0, 240) + (description.length > 240 ? '…' : '')
    : '';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: T.paper,
      padding: '40px',
      fontFamily: T.sans,
    }}>
      <div style={{
        width: '560px',
        maxWidth: '100%',
        background: T.card,
        borderRadius: T.r4,
        boxShadow: T.sh3,
        overflow: 'hidden',
      }}>

        {/* Header bar */}
        <div style={{
          background: T.mossSoft,
          padding: '16px 28px',
          borderBottom: `1.5px dashed ${T.ruleStrong}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[1, 0.7, 0.4].map((opacity, i) => (
              <div key={i} style={{
                width: '10px',
                height: '10px',
                borderRadius: T.full,
                background: T.moss,
                opacity,
                animation: `cc-pulse ${1.4 + i * 0.3}s ease-in-out infinite`,
                animationDelay: `${i * 0.2}s`,
              }} />
            ))}
          </div>
          <span style={{
            fontFamily: T.mono,
            fontSize: '12px',
            fontWeight: 700,
            color: T.mossDark,
            letterSpacing: '0.06em',
          }}>
            COOKING · please don't close this tab
          </span>
        </div>

        {/* Body */}
        <div style={{
          padding: '36px 36px 32px',
          textAlign: 'center',
        }}>
          <CottageIllustration />

          <div style={{
            fontFamily: T.hand,
            fontSize: '28px',
            color: T.orange,
            marginBottom: '6px',
          }}>
            hang tight —
          </div>
          <h1 style={{
            fontFamily: T.sans,
            fontSize: '30px',
            fontWeight: 800,
            color: T.ink,
            margin: '0 0 14px',
          }}>
            Building your plan…
          </h1>

          {/* Quote block */}
          {description && (
            <div style={{
              background: T.paperSoft,
              border: `1.5px dashed ${T.ruleStrong}`,
              borderRadius: T.r3,
              padding: '14px 16px',
              textAlign: 'left',
              marginBottom: '22px',
            }}>
              <div style={{
                fontFamily: T.mono,
                fontSize: '10px',
                fontWeight: 800,
                color: T.inkMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: '6px',
              }}>
                Your request
              </div>
              <div style={{
                fontFamily: T.sans,
                fontSize: '13px',
                color: T.ink2,
                lineHeight: 1.5,
                fontStyle: 'italic',
              }}>
                {truncatedDesc}
              </div>
            </div>
          )}

          {/* Steps list */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '18px',
            textAlign: 'left',
          }}>
            {STEPS.map((step, i) => {
              const status = i < activeStep ? 'done' : i === activeStep ? 'active' : 'pending';
              return (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  <StepIcon status={status} />
                  <span style={{
                    fontFamily: T.sans,
                    fontSize: '14px',
                    fontWeight: status === 'active' ? 800 : 500,
                    color: status === 'done' ? T.inkFaint : T.ink,
                    textDecoration: status === 'done' ? 'line-through' : 'none',
                    flex: 1,
                  }}>
                    {step}
                  </span>
                  {status === 'active' && (
                    <span style={{
                      fontFamily: T.mono,
                      fontSize: '11px',
                      color: T.orange,
                      flexShrink: 0,
                    }}>
                      …now
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div style={{
            height: '8px',
            background: T.paperDeep,
            borderRadius: T.full,
            overflow: 'hidden',
            border: `1px solid ${T.rule}`,
            marginBottom: '18px',
          }}>
            <div style={{
              height: '100%',
              width: `${Math.round(progress * 100)}%`,
              background: `linear-gradient(to right, ${T.moss}, ${T.orange})`,
              borderRadius: T.full,
              transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }} />
          </div>

          <div style={{
            fontFamily: T.hand,
            fontSize: '20px',
            color: T.orange,
            transform: 'rotate(-1deg)',
            display: 'inline-block',
          }}>
            good plans take a minute
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatingPlan;
